import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import Spacer from "@ui/spacer/Spacer";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { GD } from "src/GD";
import { getFriendlyErrorMessage } from "src/utils/errors";
import { StartScreenView } from "../StartScreenView";
import {
	AUTH_PRIMARY_BUTTON_SX,
	AUTH_SECONDARY_BUTTON_SX,
	AuthCard,
	AuthDivider,
	AuthStatus,
} from "./AuthLayout";

type ConfirmState = "confirming" | "success" | "error" | "missing";

export const ConfirmEmailPage = ({ token }: { token: string }) => {
	const [state, setState] = useState<ConfirmState>(token ? "confirming" : "missing");
	const [message, setMessage] = useState<string | null>(null);
	const [resendEmail, setResendEmail] = useState({ email: "" });
	const [resending, setResending] = useState(false);
	const started = useRef(false);

	useEffect(() => {
		if (!token || started.current) return;
		started.current = true;

		(async () => {
			const res = await GD.REQ_AUTH_REGISTER_CONFIRM.request({ token });
			if (res.error) {
				setState("error");
				setMessage(res.error.details || res.error.message || "The confirmation link is invalid or has expired.");
				return;
			}
			setState("success");
		})();
	}, [token]);

	const goToLogin = () => {
		window.location.href = "/";
	};

	const resend = async () => {
		if (!resendEmail.email.trim()) {
			toast.warning("Enter your email to resend the confirmation link");
			return;
		}
		setResending(true);
		const res = await GD.REQ_AUTH_REGISTER_RESEND.request({ email: resendEmail.email.trim() });
		setResending(false);
		if (res.error) {
			toast.error(getFriendlyErrorMessage(res.error, "Could not resend the email"));
			return;
		}
		toast.success("If the account exists, a new confirmation link is on its way");
	};

	const failed = state === "error" || state === "missing";

	return (
		<StartScreenView busy={state === "confirming"}>
			<AuthCard
				icon={state === "confirming" ? "loading" : state === "success" ? "success" : "error"}
				eyebrow="Email verification"
				title={state === "confirming" ? "Confirming your email" : state === "success" ? "Email confirmed" : "Confirmation link unavailable"}
				subtitle={state === "success" ? "Your Cardpartner account is now active." : "We use this check to protect access to your workspace."}>
				{state === "confirming" ? (
					<AuthStatus title="Activating your account">Please wait while we validate the confirmation link.</AuthStatus>
				) : null}

				{state === "success" ? (
					<>
						<AuthStatus tone="success" title="Verification complete">
							You can now sign in and continue setting up your account.
						</AuthStatus>
						<Spacer size="22px" />
						<Button title="Continue to sign in" variant="primary" onClick={goToLogin} sx={AUTH_PRIMARY_BUTTON_SX} />
					</>
				) : null}

				{failed ? (
					<>
						<AuthStatus tone="error" title={state === "missing" ? "Confirmation token is missing" : "We could not confirm this email"}>
							{state === "missing" ? "This link does not include the token needed to activate an account." : message}
						</AuthStatus>
						<Spacer size="22px" />
						<Input
							type="email"
							autoComplete="email"
							data={resendEmail}
							name="email"
							label="Account email"
							onChange={value => setResendEmail({ email: value })}
							placeholder="name@company.com"
						/>
						<Spacer size="14px" />
						<Button
							title={resending ? "Sending..." : "Resend confirmation link"}
							variant="primary"
							disabled={resending}
							onClick={resend}
							sx={AUTH_PRIMARY_BUTTON_SX}
						/>
						<AuthDivider />
						<Button title="Back to sign in" variant="secondary" onClick={goToLogin} sx={AUTH_SECONDARY_BUTTON_SX} />
					</>
				) : null}
			</AuthCard>
		</StartScreenView>
	);
};
